import React, { useState, useCallback, useEffect } from 'react';
import { useI18n } from '../../contexts/I18nContext';

interface SessionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateSession: (name: string) => void;
  onJoinSession: (sessionId: string, userName: string) => void;
  /** URL에서 전달된 초기 세션 ID (있으면 자동으로 Join 모드로 진입) */
  initialSessionId?: string | null;
}

type DialogMode = 'menu' | 'create' | 'join';

/**
 * Session creation and joining dialog
 */
export const SessionDialog: React.FC<SessionDialogProps> = ({
  isOpen,
  onClose,
  onCreateSession,
  onJoinSession,
  initialSessionId,
}) => {
  const { t } = useI18n();
  const [mode, setMode] = useState<DialogMode>('menu');
  const [userName, setUserName] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [error, setError] = useState('');

  // initialSessionId가 있으면 자동으로 Join 모드로 진입
  useEffect(() => {
    if (isOpen && initialSessionId) {
      setMode('join');
      setSessionId(initialSessionId);
    }
  }, [isOpen, initialSessionId]);

  /**
   * Reset dialog state
   */
  const resetState = useCallback(() => {
    setMode('menu');
    setUserName('');
    setSessionId('');
    setError('');
  }, []);

  /**
   * Handle dialog close
   */
  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  /**
   * Handle create session
   */
  const handleCreate = useCallback(() => {
    if (!userName.trim()) {
      setError(t.collaboration.pleaseEnterName);
      return;
    }
    onCreateSession(userName.trim());
    handleClose();
  }, [userName, onCreateSession, handleClose, t]);

  /**
   * Handle join session
   */
  const handleJoin = useCallback(() => {
    if (!userName.trim()) {
      setError(t.collaboration.pleaseEnterName);
      return;
    }
    if (!sessionId.trim()) {
      setError(t.collaboration.pleaseEnterSessionId);
      return;
    }
    onJoinSession(sessionId.trim().toUpperCase(), userName.trim());
    handleClose();
  }, [userName, sessionId, onJoinSession, handleClose, t]);

  /**
   * Handle key press
   */
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (mode === 'create') {
          handleCreate();
        } else if (mode === 'join') {
          handleJoin();
        }
      } else if (e.key === 'Escape') {
        if (mode === 'menu') {
          handleClose();
        } else {
          setMode('menu');
          setError('');
        }
      }
    },
    [mode, handleCreate, handleJoin, handleClose]
  );

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          minWidth: '360px',
          maxWidth: '400px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyPress}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 600,
              color: '#1F2937',
            }}
          >
            {mode === 'menu' && t.collaboration.title}
            {mode === 'create' && t.collaboration.createSession}
            {mode === 'join' && t.collaboration.joinSession}
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#6B7280',
              fontSize: '20px',
              lineHeight: 1,
            }}
          >
            x
          </button>
        </div>

        {/* Menu mode */}
        {mode === 'menu' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p
              style={{
                margin: '0 0 12px 0',
                color: '#6B7280',
                fontSize: '14px',
              }}
            >
              {t.collaboration.startNewSession}
            </p>

            <button
              onClick={() => setMode('create')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                backgroundColor: '#F3F4F6',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#E5E7EB')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#F3F4F6')}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  backgroundColor: '#3B82F6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FFFFFF',
                  fontSize: '18px',
                }}
              >
                +
              </div>
              <div>
                <div style={{ fontWeight: 600, color: '#1F2937' }}>{t.collaboration.createSession}</div>
                <div style={{ fontSize: '13px', color: '#6B7280' }}>
                  {t.collaboration.startSession}
                </div>
              </div>
            </button>

            <button
              onClick={() => setMode('join')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                backgroundColor: '#F3F4F6',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#E5E7EB')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#F3F4F6')}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  backgroundColor: '#22C55E',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FFFFFF',
                  fontSize: '18px',
                }}
              >
                &rarr;
              </div>
              <div>
                <div style={{ fontWeight: 600, color: '#1F2937' }}>{t.collaboration.joinSession}</div>
                <div style={{ fontSize: '13px', color: '#6B7280' }}>
                  {t.collaboration.joinWithId}
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Create mode */}
        {mode === 'create' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                }}
              >
                {t.collaboration.yourName}
              </label>
              <input
                type="text"
                value={userName}
                onChange={(e) => {
                  setUserName(e.target.value);
                  setError('');
                }}
                placeholder={t.collaboration.enterName}
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {error && (
              <div
                style={{
                  color: '#EF4444',
                  fontSize: '13px',
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setMode('menu');
                  setError('');
                }}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {t.collaboration.back}
              </button>
              <button
                onClick={handleCreate}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {t.collaboration.create}
              </button>
            </div>
          </div>
        )}

        {/* Join mode */}
        {mode === 'join' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                }}
              >
                {t.collaboration.yourName}
              </label>
              <input
                type="text"
                value={userName}
                onChange={(e) => {
                  setUserName(e.target.value);
                  setError('');
                }}
                placeholder={t.collaboration.enterName}
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                }}
              >
                {t.collaboration.sessionId}
              </label>
              <input
                type="text"
                value={sessionId}
                onChange={(e) => {
                  setSessionId(e.target.value.toUpperCase());
                  setError('');
                }}
                placeholder={t.collaboration.enterSessionId}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              />
            </div>

            {error && (
              <div
                style={{
                  color: '#EF4444',
                  fontSize: '13px',
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setMode('menu');
                  setError('');
                }}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {t.collaboration.back}
              </button>
              <button
                onClick={handleJoin}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: '#22C55E',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {t.collaboration.join}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionDialog;
